/**
 * M4.4 (Juni 2026) – Reine, testbare VORBEREITUNG der Rechts-/Darstellungsschicht.
 *
 * Ziel: Die AGB §6-Preistabelle SOLL künftig ausschließlich aus einer AUSDRÜCKLICH
 * veröffentlichten Catalog-Version ableitbar sein – niemals aus CURRENT und niemals
 * automatisch.
 *
 * Stand M4.4 (bewusst):
 *   - `LEGAL_PUBLISHED_CATALOG_VERSION = null` → es ist KEINE Version als Rechtsgrundlage
 *     gebunden. Damit liefern die Funktionen `null` und die AGB behalten ihre statischen,
 *     aktuell IN KRAFT befindlichen Preise (legalContent.js, §6). KEIN Preis-Launch.
 *   - KEINE Verdrahtung mit der API, KEINE CURRENT-Nutzung, KEINE Billing-/Resolver-Logik.
 *   - Grandfathering bleibt vollständig unangetastet.
 *
 * Diese Datei ist absichtlich self-contained (keine Kopplung an andere Module), damit die
 * Erzeugung rechtlich relevanter Texte nicht versehentlich durch fremde Änderungen driftet.
 */

// Explizite Rechtsgrundlage-Bindung. NIEMALS auf CURRENT setzen; ausschließlich eine
// ausdrücklich veröffentlichte (public_visible=true) Katalog-Version eintragen.
// M4.7 LAUNCH GATE (2026-06): bewusst gebunden an die veröffentlichte OBD-C-Version
// 2026-08-01. AGB §6 löst ausschließlich gegen genau diese Version auf (Traceability).
export const LEGAL_PUBLISHED_CATALOG_VERSION = "2026-08-01";

function eurosFromCents(cents) {
  if (cents == null) return null;
  return (cents / 100).toLocaleString("de-DE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function seatLabel(seatMin, seatMax) {
  if (seatMax == null) return `ab ${seatMin} Mitarbeiter`;
  return `${seatMin}–${seatMax} Mitarbeiter`;
}

function priceText(monthlyCents, yearlyCents) {
  return `${eurosFromCents(monthlyCents)} € netto / Monat · ${eurosFromCents(yearlyCents)} € netto / Jahr`;
}

/**
 * Erzeugt die AGB §6-Preiszeilen (kv-Format `[label, value]`) AUSSCHLIESSLICH aus einer
 * ausdrücklich gebundenen, veröffentlichten Catalog-Version.
 *
 * Liefert `null` (→ statische In-Kraft-Zeilen bleiben maßgeblich), wenn:
 *   - keine Version gebunden ist (`boundVersion` falsy), ODER
 *   - der Katalog nicht veröffentlicht ist (`published !== true` / leer), ODER
 *   - die veröffentlichte Version NICHT exakt der gebundenen Version entspricht (Traceability).
 *
 * Es wird ausschließlich der explizit übergebene (öffentliche) Katalog ausgewertet –
 * NIEMALS CURRENT, NIEMALS eine unveröffentlichte Version.
 */
export function buildAgbPriceRows(publishedCatalog, boundVersion = LEGAL_PUBLISHED_CATALOG_VERSION) {
  if (!boundVersion) return null;
  if (!publishedCatalog || publishedCatalog.published !== true) return null;
  if (!Array.isArray(publishedCatalog.products) || publishedCatalog.products.length === 0) return null;
  if (publishedCatalog.version !== boundVersion) return null;

  return publishedCatalog.products
    .filter((p) => p && p.visible_on_landing)
    .map((p) => {
      const pricing = p.pricing || {};
      const enterprise = !!p.is_custom || pricing.model === "custom";
      const key = `${p.label} (${seatLabel(p.seat_min, p.seat_max)})`;
      const value = enterprise
        ? "individuelles Angebot"
        : priceText(pricing.monthly_net_cents, pricing.yearly_net_cents);
      return [key, value];
    });
}

/**
 * Bequemer Einstieg für die spätere Verwendung in der Rechts-/Darstellungsschicht.
 * Nutzt die fest gebundene `LEGAL_PUBLISHED_CATALOG_VERSION`. Solange diese `null` ist,
 * liefert die Funktion `null` → die statischen AGB-Preise bleiben in Kraft.
 */
export function resolveAgbPriceRows(publishedCatalog) {
  return buildAgbPriceRows(publishedCatalog, LEGAL_PUBLISHED_CATALOG_VERSION);
}
